import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../services/auth.service.optimized';
import { jwtDecode } from 'jwt-decode';

interface ProtectedRouteProps {
    children: React.ReactNode;
    allowedRoles?: string[];
}

interface DecodedToken {
    id: string;
    company: string | null;
    role: string;
    iat: number;
    exp: number;
}

const VALID_ROLES = ['superadmin', 'admin', 'member'];

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
    const token = authService.getToken();
    const location = useLocation();

    if (!token) {
        return <Navigate to="/login" replace />;
    }

    try {
        const decoded = jwtDecode<DecodedToken>(token);
        
        if (decoded.exp * 1000 < Date.now()) {
            authService.logout();
            return <Navigate to="/login" replace />;
        }

        // Reject unknown roles (e.g. stale token with role "user") immediately
        if (!VALID_ROLES.includes(decoded.role)) {
            console.warn(`[ProtectedRoute] Unknown role "${decoded.role}", forcing re-login`);
            authService.logout();
            return <Navigate to="/login" replace />;
        }

        if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(decoded.role)) {
            console.warn(`[ProtectedRoute] Role "${decoded.role}" not allowed on ${location.pathname}`);
            if (decoded.role === 'superadmin') {
                return <Navigate to="/superadmin/dashboard" replace />;
            }
            // Avoid redirect loop: don't redirect to the same path
            const target = '/dashboard';
            if (location.pathname === target) {
                authService.logout();
                return <Navigate to="/login" replace />;
            }
            return <Navigate to={target} replace />;
        }

        return <>{children}</>;

    } catch (error) {
        console.error('[ProtectedRoute] Invalid token:', error);
        authService.logout();
        return <Navigate to="/login" replace />;
    }
}; 